import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@fieldcheck/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * match **ZERO rows and return "success"**. This is not a hypothesis: it was
 * measured in `space-academy-app` (same canon, local stack's `activity` table,
 * course `06a131d0`) — the course is created, 1.03 s later the unit is created, and
 * **between them there is NO `course/updated` op**: the title the author had just
 * written never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint does not yet bring the
 * contain the newly created row, the row is **ABSENT for that instant** — an
 * UPDATE against it writes nothing, generates no op, and **does not fail**.
 *
 * In FieldCheck the damage is worse than in other clones: the mutations touched
 * here are inspection reports, findings and checklists that the field
 * operator believes to have saved — and `template`/`inspection`/`finding` are EVIDENCE
 * (compliance, immutable soft-delete, see `fieldwork/mutations.ts`). Losing in
 * silence the title of a finding or the status of an inspection is not just
 * an ugly datum: it is a compliance record that never existed.
 *
 * Why the absence of an error proves nothing here: the server's write path
 * returns **2xx deliberately** (a 4xx would block the client's queue forever).
 * queue forever, see `infra/PENDING_WORK.md` §1.5.7). That case is a
 * DIFFERENT path — there the server rejects and silences —, but the criterion is
 * the same: a user write that disappears without trace is the worst failure
 * mode.
 *
 * Contract of this module: write, **REREAD**, compare. If the value did not
 * land it retries with backoff; if upon exhaustion it still has not landed, it
 * **THROWS** — caller communicates it instead of leaving user staring at a
 * field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberate) ───────────────────────────
 * Hard `DELETE`s are not verified: their natural check would be "the row is
 * no longer there", but "absent" is precisely the ambiguous state in this
 * window — one cannot distinguish "it was deleted" from "it had not yet arrived". In FieldCheck moreover
 * there is almost no hard `DELETE`: `template`/`inspection`/`finding` are SOFT-DELETE
 * (evidence trash, never destroyed); the remaining real `.delete()` calls
 * (`deleteSiteMutation`, `deleteTemplateItemMutation`,
 * `deleteFieldworkAttachmentMutation`) delete auxiliary records without compliance
 * traces. Signal that would reopen this: a purge that genuinely gets lost
 * (row reappearing in trash after purge).
 *
 * ── `scope`: tenant isolation, not just correctness ──────────────────────────
 * Brought from `comandero-app` (`f8f1049`) and `listto-app` hosts it for
 * multi-org clones — FieldCheck is one of them (`infra/sync_streams.yaml`,
 * stream `org_fieldwork`: synchronizes ALL organizations where the
 * user is `member`, not only the active one). **Not activated in the conversion of
 * this repo**: unlike `comandero`, no FieldCheck mutation
 * filtered by `organization_id` in the `WHERE` before this conversion (only
 * `eq(id)`) — there is no existing tenant filter that mechanical conversion
 * could drop. Adding `scope` here would be a NEW authorization
 * boundary (they would lack active `organizationId` plumbing in
 * each `update*Mutation` and their 3 call-sites in `apps/web/src/features/**`),
 * and the server (`SyncRepository`) already re-validates permissions on upload — it is
 * a decision for the authorization campaign (`RBAC_AUTHZ_HARDENING`), not for
 * §4.11. Kept HOSTED here, ready for when that boundary is decided.
 *
 * ── THREE pieces involving `updated_at`, and they are THREE different things ──
 * They overlap in the column name but not in behavior — each
 * has its own flag, and none implies the others:
 *
 *   1. **Stamping** (`hasUpdatedAt`, default `true`): whether this helper
 *      writes `updated_at` in the `SET` on each attempt. All tables protected
 *      by this repo (`site`, `template`, `template_item`, `inspection`,
 *      `inspection_answer`, `finding`, `user`) DO have `updated_at`
 *      (verified against `packages/powersync/src/schema.ts`), so no
 *      FieldCheck mutation needs `hasUpdatedAt: false` today — the flag
 *      remains hosted for future tables that lack it.
 *   2. **Ignore any `updated_at` supplied by the `patch`** (always, with no flag):
 *      it is never compared or passed through to SET unchanged.
 *   3. **Verify BY `updated_at`** (`verifyBy: "updated_at"`, opt-in): use
 *      the stamped timestamp as proof of landing instead of comparing the
 *      patch by value. No FieldCheck mutation uses atomic SQL
 *      increments today, so it has no caller either — hosted likewise.
 *
 * ── Why there is NO `patchRowsVerified` / `waitForRowsPresent` (batch variants) ─
 * `folio-office-app` and `apunte-digital-app` added them to move a
 * cohort of rows at once (`WHERE id IN (...)`). FieldCheck has no
 * such mutation (verified: no `inArray` in
 * `packages/shared/src/fieldwork/mutations.ts` nor in `users/mutations.ts`) —
 * bringing them would be caller-less code. Signal that would reopen this: a trash
 * by cohort (restoring several inspections/findings at once).
 *
 * ── Second class: an upsert that chooses INSERT-vs-UPDATE with its own SELECT ──
 *
 * `patchRowVerified`/`waitForRowPresent` protect the write against a row
 * whose `id` is ALREADY known. There is a different class that neither covers:
 * an upsert that decides whether to INSERT or UPDATE with its own SELECT because there is no
 * `id` to reread until that branch is chosen. That SELECT falls into the SAME
 * checkpoint window: if the row exists but has just reappeared after a
 * rebuild, the SELECT sees it as missing and the code INSERTs a new row—the data
 * is not lost, the row is DUPLICATED, and no error is visible.
 *
 * **FieldCheck does NOT have this class** (verified, not assumed: `git grep -n -B8
 * '\.insert(' packages/shared/src/**\/mutations.ts` finds no decisive SELECT
 * before an insert/update branch — all `create*Mutation` calls
 * generate `id` with `generateId()` and insert directly; the only mutation that
 * might appear to do so, `upsertAnswerMutation`, already receives explicit `existingId`
 * from the caller — the UI keeps the answer map in memory and knows whether a row
 * already exists, so there is no decisive SELECT to protect).
 * `readMatchingRowVerified` is hosted just like in the canon, for any clone that does land
 * in this shape. The complete guide (with criteria for when to use each path)
 * is in `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §3.
 */

/**
 * Retry backoff. Sums ≈ 6.05 s: more than covers the round trip of a
 * a checkpoint (measured at ~1 s against the local stack) without leaving a
 * fast interaction —completing an item, closing a finding— hanging
 * indefinitely when the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition—deep tenant isolation. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Delays between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Additional conditions the row must ALSO satisfy, besides `id`—
   * typically `organization_id`. They apply to both the UPDATE and the
   * reread: a row that does not satisfy the scope is treated like a
   * absent row (see security note above). Hosted; no mutation in this
   * repo uses it today (see note above).
   */
  scope?: readonly ScopeCondition[];
  /**
   * Whether the table has an `updated_at` column. Defaults to `true` (the
   * usual behavior: it is stamped on every attempt). Set it to `false`
   * on tables WITHOUT that column—with `false`, `patchRowVerified`
   * never puts it in the `SET`, and `verifyBy: "updated_at"` ceases to be valid.
   */
  hasUpdatedAt?: boolean;
  /**
   * How to verify that the write landed when the patch cannot be
   * compare by value (e.g. an atomic SQL increment expression).
   * EXPLICIT mode, never automatic. `"updated_at"` compares the stamp that
   * this helper stamps on each attempt: proves that THIS write landed,
   * not that the final value is expected. Requires `hasUpdatedAt` distinct
   * from `false`.
   */
  verifyBy?: "updated_at";
}

/** Error for a local-first write that failed to land in view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` with no scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it is not in the
 * local view or does not satisfy the scope—the same result as a "missing row",
 * deliberately (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`: the select
  // resolves to `{ [x: string]: any }[]`, so annotating the destination is enough—no
  // need to go through `unknown`; that would narrow without any real check.
  const rows: Record<string, unknown>[] = await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1);
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * fields and retries as long as they have not landed. Throws `LocalWriteLostError`
 * if they are still missing upon exhausting the backoff.
 *
 * If `options.hasUpdatedAt` is not `false` (the normal case), this function sets
 * `updated_at` on every attempt—any `updated_at` supplied by the
 * `patch` itself is ALWAYS ignored, both when writing and when comparing; the rest of
 * the `patch` must contain only domain fields. With `hasUpdatedAt:
 * false` the `SET` does not touch it at all. With `options.verifyBy ===
 * "updated_at"` the patch is not compared by value: it checks that the stamp
 * of THIS attempt landed — combining it with `hasUpdatedAt: false` is a configuration
 * error and throws immediately, before writing anything.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is not compatible with hasUpdatedAt: false — the table does not have that column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it (+ `scope`,
 * if given, with the same tenant-isolation criterion as `patchRowVerified`).
 *
 * For mutations that are not a simple patch —in this repo,
 * `restoreTemplateVersionMutation`, which inside ONE transaction updates
 * `template` and RECREATES its `template_item` set. Retrying inside the
 * transaction is invalid there (it would sleep holding the write lock), and
 * if `templateId` is not yet in the view the `UPDATE` matches nothing but
 * the `DELETE`+`INSERT` of items still occurs: the template is left with the items
 * of the restored version but the old `name`/`description` — an inconsistent
 * mix, not just a lost field.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Re-reads, with the same backoff as the rest of the module, a row that is
 * identified by CONTENT instead of by `id` — Class 2 of the catalog (see
 * the "Second class" note above). **FieldCheck has no caller for this today**
 * (verified, not assumed — see note above): lives here HOSTED,
 * copied as-is from the canon (`listto-app`, originally brought from
 * `stockpilot-app@b8c4bc8a`), for the day a mutation in this repo
 * decides INSERT-vs-UPDATE by content instead of known `id`.
 *
 * It is ALWAYS called before opening the transaction that chooses INSERT-vs-UPDATE,
 * and NEVER inside it—for the same reason as `waitForRowPresent`. When there are
 * several lines to resolve, resolve them in PARALLEL (`Promise.all`) before
 * the transaction.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting the backoff
 * is NOT an error here: genuine absence (the first time that content
 * combination is touched) is a VALID result, so this function
 * returns `undefined` instead of throwing, and caller proceeds to INSERT
 * exactly as if it had not retried.
 *
 * `read` must repeat the SAME query on every attempt—a function that
 * rebuilds the `where` against `db`/`trx`, not an already-resolved promise
 * that would be reused without querying the database again.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
